<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">{{
      $t("selfie.pageTitle")
    }}</app-top-bar>
    <v-main
      :style="mainStyle"
      :class="hasBackgroundImage ? '' : 'bg-background'"
      class="text-on-surface-variant"
    >
      <v-container v-if="!showComplete && !util.isMobile">
        <media-desktop-handoff :description="$t('selfie.handoffDescription')" />
      </v-container>

      <v-container v-if="!showComplete && util.isMobile">
        <h1 class="page-title mb-6">{{ title }}</h1>
        <p class="mb-0" v-html="subtitle" />

        <v-sheet v-if="!isSelfieAvailable" class="d-flex flex-column ga-9 mt-12">
          <div v-for="(rule, index) in rules" :key="'rule' + index" class="d-flex flex-column">
            <img
              class="mx-auto mb-3"
              :src="rule.img"
              width="70"
              :class="hasBackgroundImage ? 'invert' : ''"
            />
            <p class="text-center mb-0" v-html="rule.text" />
          </div>
        </v-sheet>

        <img
          v-if="isSelfieAvailable"
          class="img-preview rounded-lg mt-6"
          :src="selfieUrl"
          style="max-width: 500px"
        />

        <image-uploader
          ref="selfieInputRef"
          accept="image/jpeg,image/png"
          :capture="'user'"
          :max-height="1920"
          :max-width="1920"
          output-format="file"
          :preview="false"
          :quality="0.9"
          @input="onSelfieInput"
        />
        <v-overlay :model-value="isProcessingMedia" class="align-center justify-center"
          ><v-progress-circular color="primary" indeterminate size="64"></v-progress-circular
        ></v-overlay>
      </v-container>

      <v-container v-if="showComplete">
        <phase-completed />
      </v-container>
    </v-main>

    <app-footer v-if="util.isMobile">
      <template v-if="!isSelfieAvailable">
        <continue-footer-button color="primary" :loading="isAdvancing" @click="onPromptSelfie">
          {{ $t("selfie.continueButton") }}
        </continue-footer-button>
      </template>
      <template v-else>
        <footer-button color="primary" :loading="isAdvancing" @click="advance">
          {{ $t("selfie.looksGoodButton") }}
        </footer-button>
        <footer-button variant="text" :disabled="isAdvancing" @click="onPromptSelfie">
          {{ $t("selfie.takeAnotherButton") }}
        </footer-button>
      </template>
    </app-footer>
  </v-app>
</template>

<script setup lang="ts">
  import imageCompression from "browser-image-compression";
  import { computed, ref } from "vue";
  import { useI18n } from "vue-i18n";
  import ImageUploader from "vue3-image-upload-resize";

  import cap from "@/assets/images/icons/cap.svg";
  import frame from "@/assets/images/icons/frame.svg";
  import glasses from "@/assets/images/icons/glasses.svg";
  import { usePageBackground } from "@/composables/usePageBackground";
  import { useStepFlow } from "@/composables/useStepFlow";
  import { useUtils } from "@/composables/useUtils";
  import { useApiStore } from "@/stores/api";
  import { useAppStore } from "@/stores/app";

  const { t } = useI18n();

  const api = useApiStore();
  const app = useAppStore();
  const { mainStyle, hasBackgroundImage } = usePageBackground();
  const util = useUtils();

  const isProcessingMedia = ref(false);
  const selfie = ref<File | null>(null);
  const selfieInputRef = ref<InstanceType<typeof ImageUploader> | null>(null);
  const pathPage = "/selfie";

  const {
    isAdvancing,
    showComplete,
    advance: advanceFlow,
  } = useStepFlow(pathPage, () => isComplete.value);

  const isComplete = computed(() => api.isItemComplete("selfie"));
  const isSelfieAvailable = computed(() => !isComplete.value && selfie.value !== null);
  const selfieUrl = computed(() => (selfie.value ? URL.createObjectURL(selfie.value) : ""));

  const title = computed(() =>
    isSelfieAvailable.value ? t("selfie.titleReview") : t("selfie.titleTake"),
  );
  const subtitle = computed(() =>
    isSelfieAvailable.value ? t("selfie.subtitleReview") : t("selfie.subtitleTake"),
  );

  async function onSelfieInput(file: File) {
    isProcessingMedia.value = true;
    await new Promise((resolve) => setTimeout(resolve, 150)); // Garante o render do overlay na tela
    try {
      if (!(file instanceof File)) return;
      selfie.value =
        file.size > 3 * 1024 * 1024
          ? await imageCompression(file, {
              maxSizeMB: 3,
              useWebWorker: true,
              maxWidthOrHeight: 1920,
            })
          : file;
    } catch (error) {
      console.error(error);
      util.noticeError(error);
    } finally {
      isProcessingMedia.value = false;
    }
  }

  async function onPromptSelfie() {
    if (isComplete.value) {
      await advanceFlow();
      return;
    }
    const input = selfieInputRef.value?.$el.querySelector("input[type=file]") as HTMLInputElement;
    input?.click();
  }

  async function advance() {
    try {
      await advanceFlow(async () => {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          if (selfie.value) reader.readAsDataURL(selfie.value);
        });
        await api.setSelfie(base64);
      });
    } catch (error) {
      util.noticeError(error);
    }
  }

  const rules = [
    { img: cap, text: t("selfie.rule1Text") },
    { img: glasses, text: t("selfie.rule2Text") },
    { img: frame, text: t("selfie.rule3Text") },
  ];
</script>

<style scoped>
  :deep(.img-preview) {
    height: 100% !important;
    width: 100% !important;
  }

  :deep(.fileinput) {
    display: none;
  }

  p {
    margin-bottom: 1.25rem;
  }

  .invert {
    filter: invert(1);
  }
</style>
